// src/store/authStore.js
// Zustand tabanlı Auth + profil store — pilot implementasyon
// App.js'teki user, currentUserProfile, loading state'lerini merkezi yönetir
// Diğer bileşenler import ederek doğrudan erişebilir — prop drilling gereksiz

import { create } from "zustand";
import type { StoreApi } from "zustand";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import type { User } from "firebase/auth";
import { doc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import type { DocumentData, Unsubscribe } from "firebase/firestore";

/**
 * Store'da tutulan profil: `users/{uid}` dokümanının HAM verisi + `uid`.
 * (`{ uid: firebaseUser.uid, ...snap.data() }` — gövdeden.) Alan alan
 * sayılmadı: doküman şeması Firestore'da yaşıyor, burada kopyası çürürdü.
 */
export type KullaniciProfilDokumani = DocumentData & { uid: string };

/** Store'un ŞEKLİ GÖVDEDEN: yedi state alanı + beş aksiyon. */
export type KimlikDurumu = {
  user: User | null;
  currentUserProfile: KullaniciProfilDokumani | null;
  loading: boolean;
  profileResolved: boolean;
  profileMissing: boolean;
  profileUnreachable: boolean;
  profileRetrying: boolean;
  setUser: (user: User | null) => void;
  setCurrentUserProfile: (profile: KullaniciProfilDokumani | null) => void;
  setLoading: (loading: boolean) => void;
  retryProfile: () => void;
  /** Auth dinleyicisini kurar; temizleme fonksiyonu döner. */
  initAuth: () => () => void;
};

type Ayarla = StoreApi<KimlikDurumu>["setState"];
type Getir = StoreApi<KimlikDurumu>["getState"];

const isPermDenied = (err?: { code?: unknown; message?: string } | null) =>
  err?.code === "permission-denied" || err?.message?.includes("Missing or insufficient permissions");

let profileUnsub: Unsubscribe | null = null;
// EMİR #94.3 — YANLIŞ-YOKLUK KAPISI: sunucu snapshot'ı gelmezse sonsuza kadar
// beklemeyi ÖNLEYEN sınırlı bekleyiş zamanlayıcısı (C4: sistem hiç yanlış
// yapmadan sonsuza kadar takılabilir).
let profilBeklemeTimer: ReturnType<typeof setTimeout> | null = null;
const PROFIL_BEKLEME_MS = 8000;
const beklemeyiDurdur = () => {
  if (profilBeklemeTimer) { clearTimeout(profilBeklemeTimer); profilBeklemeTimer = null; }
};
/** Sınırlı bekleyişi başlat (zaten sayıyorsa dokunma). */
const beklemeyiBaslat = (set: Ayarla, get: Getir) => {
  if (profilBeklemeTimer) return;
  profilBeklemeTimer = setTimeout(() => {
    profilBeklemeTimer = null;
    // Hâlâ çözülmediyse kullanıcıyı BİLGİLENDİR — sonsuz bekleme YOK (C4).
    // Süre doldu: hâlâ çözülmediyse "bağlantı yok" durumunda KAL (veya oraya
    // geç) ve tekrar denenebilir hâle dön.
    if (!get().profileResolved) set({ profileUnreachable: true, profileRetrying: false });
  }, PROFIL_BEKLEME_MS);
};
// PR-36 — presenceInterval KALDIRILDI (60sn hot heartbeat yok). presenceVisHandler
// hem visibilitychange hem window focus için ortak dinleyici.
let presenceVisHandler: (() => void) | null = null;

export const useAuthStore = create<KimlikDurumu>((set, get) => ({
  // ─── State ───────────────────────────────────────────────────────────────
  user: null,
  currentUserProfile: null,
  loading: true,
  // Sosyal giriş için: profil snapshot'ı çözüldü mü + doc YOK mu?
  // profileResolved=false iken (ilk snapshot beklenirken) guard tetiklenmez →
  // mevcut kullanıcı davranışı değişmez. Yalnız sosyal yeni kullanıcıda
  // (doc yok) profileMissing=true olur → App profil-tamamlama ekranı gösterir.
  profileResolved: false,
  profileMissing: false,
  // EMİR #94.3 — profil ne OKUNABİLDİ ne de YOKLUĞU sunucudan doğrulandı.
  // "profil yok"tan AYRI bir durumdur; ekranda "bağlantı kurulamadı" gösterilir.
  profileUnreachable: false,
  // "Tekrar dene"ye basıldı, sonuç bekleniyor. Ayrı bayrak ŞART: yalnızca
  // profileUnreachable'ı false yapmak, profileResolved hâlâ false iken App'in
  // ana arayüzü PROFİLSİZ render etmesine yol açıyordu → kullanıcıya sahte
  // "düzeldi" hissi. Ekran yerinde kalır, yalnız düğme "Deniyor…" olur.
  profileRetrying: false,

  // ─── Actions ─────────────────────────────────────────────────────────────
  setUser: (user: User | null) => set({ user }),
  setCurrentUserProfile: (profile: KullaniciProfilDokumani | null) =>
    set({ currentUserProfile: profile }),
  setLoading: (loading: boolean) => set({ loading }),

  /**
   * "Tekrar dene" — bağlantı-yok ekranındaki tek eylem.
   * Firestore SDK zaten arka planda yeniden deniyor; burada yapılan, kullanıcıyı
   * bekleyişe geri almak ve sayacı yeniden kurmak. (Tam yeniden yükleme
   * gerekmez; sunucu snapshot'ı gelir gelmez ilgili dal durumu düzeltir.)
   */
  retryProfile: () => {
    // (store artık tipli → `get()` doğrudan okunur; eski `as any` düştü.)
    if (get().profileRetrying) return;            // çift tıklama koruması
    beklemeyiDurdur();
    set({ profileRetrying: true });               // ekran KALIR, düğme meşgul
    beklemeyiBaslat(set, get);
  },

  /**
   * Auth dinleyicisini başlat — App mount'ta bir kez çağrılır.
   * Temizleme fonksiyonu döner (useEffect cleanup için).
   */
  initAuth: () => {
    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      // Önceki profil snapshot'ını temizle
      if (profileUnsub) {
        profileUnsub();
        profileUnsub = null;
      }
      // PR-36 — presence dinleyicilerini temizle (visibilitychange + focus).
      if (presenceVisHandler) {
        if (typeof document !== "undefined") document.removeEventListener("visibilitychange", presenceVisHandler);
        if (typeof window !== "undefined") window.removeEventListener("focus", presenceVisHandler);
        presenceVisHandler = null;
      }

      if (firebaseUser) {
        // Yeni kullanıcı oturumu — profil çözümünü sıfırla (guard beklesin).
        set({ user: firebaseUser, loading: false, profileResolved: false, profileMissing: false });

        // Profil snapshot başlat
        const ref = doc(db, "users", firebaseUser.uid);
        // ══════════════════════════════════════════════════════════════════
        // EMİR #94.3 — YANLIŞ YOKLUK (sanal dünya yönlendirilmiş koşusunun bulgusu)
        //
        // BULGU: firestore t=0'dan kapalıyken profili TAM olan kullanıcıya
        // 20 saniye boyunca "Kullanıcı adını seç" onboarding ekranı gösteriliyordu
        // (kontrol kolu aynı anda normal boş-akış ekranını veriyordu). Sebep
        // aşağıdaki `else` dalıydı.
        //
        // KÖK: `snap.exists() === false` "doküman YOK" DEMEK DEĞİLDİR. Firestore
        // sunucuya ulaşamadığında elindeki (burada BOŞ, çünkü memoryLocalCache)
        // önbellekten bir snapshot üretir → exists=false + metadata.fromCache=true.
        // Ayırt edici alan `snap.metadata.fromCache`.
        //
        // 🔑 ASİMETRİ (kasıtlı): VARLIK önbellekten gelse bile GÜVENİLİR — veri
        // uydurulamaz. YOKLUK yalnız SUNUCUDAN geldiğinde güvenilirdir; kanıtın
        // yokluğu, yokluğun kanıtı değildir.
        //
        // ⏳ C4 (mimar şartı): "yalnız !fromCache" tek başına GÜVENLİK hatasını
        // CANLILIK hatasına çevirirdi — kalıcı çevrimdışı kullanıcı sonsuza kadar
        // askıda kalırdı. Bu yüzden bekleyiş SINIRLI; süre dolunca açık
        // "bağlantı kurulamadı · tekrar dene" durumu. Bu repo aynı dersi bir kez
        // canlıda ödedi (App.tsx'teki e-posta doğrulama kapısı kullanıcıları
        // kalıcı kilitlemişti) — tekrarlanmıyor.
        // ══════════════════════════════════════════════════════════════════
        beklemeyiDurdur();
        profileUnsub = onSnapshot(
          ref,
          (snap) => {
            const sunucudan = snap.metadata?.fromCache === false;
            if (snap.exists()) {
              // VARLIK: önbellekten gelse de gerçektir.
              beklemeyiDurdur();
              set({
                currentUserProfile: { uid: firebaseUser.uid, ...snap.data() },
                profileResolved: true,
                profileMissing: false,
                profileUnreachable: false,
                profileRetrying: false,
              });
            } else if (sunucudan) {
              // YOKLUK, SUNUCU onayıyla: sosyal yeni kullanıcı (veya henüz oluşmadı).
              beklemeyiDurdur();
              set({
                currentUserProfile: null,
                profileResolved: true,
                profileMissing: true,
                profileUnreachable: false,
                profileRetrying: false,
              });
            } else {
              // Önbellekten BOŞ snapshot: "yok" değil, "bilmiyorum". Bekle —
              // ama süresiz değil.
              beklemeyiBaslat(set, get);
            }
          },
          (err) => {
            // permission-denied: auth henüz hazır değilken gelen geçici hata — sessizce yut
            if (!isPermDenied(err)) {
              console.warn("[authStore] profil-snap hatası:", err?.message);
            }
            // Hata geldiyse sunucu snapshot'ı hiç gelmeyebilir → sayaç başlasın.
            beklemeyiBaslat(set, get);
          }
        );

        // PR-36 — PRESENCE COST P0: 60sn'lik SICAK heartbeat KALDIRILDI.
        //  Neden: her `users/{uid}` yazımı, o dokümanı dinleyen 3 v1 onWrite
        //  trigger'ını (content_privacy_denorm, posts_denorm, referral_autoconnect)
        //  INVOKE ediyordu — hepsi erken-return yapsa bile invoke faturalanıyor.
        //  60sn × 3 trigger × N aktif kullanıcı = en büyük gereksiz maliyet kalemi.
        //  Çözüm (owner Tercih B): lastSeen artık YALNIZ düşük-frekanslı olaylarda
        //  yazılır — login (1×), sekme görünür olunca (visibilitychange), pencere
        //  focus. lastSeen users/{uid}'de KALIR → DM "Aktif" + harita okuyucuları
        //  AYNI doc'tan okumaya devam (yeni path/N+1 YOK, rules/functions YOK).
        //  `online: true` zaten ölü flag'di (kaldırılmıştı). isActiveNow TTL 2.5dk
        //  korunur; sürekli-görünür statik sekmede nokta TTL sonrası soğuyabilir
        //  (focus/visibility/navigasyon tazeler) — kabul edilen takas.
        const updatePresence = async () => {
          try {
            if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
            await updateDoc(ref, { lastSeen: serverTimestamp() });
          } catch {}
        };
        updatePresence(); // login anında bir kez
        // Sekme tekrar görünür olunca + pencere focus'unda tazele (hot interval YOK).
        if (typeof document !== "undefined") {
          presenceVisHandler = () => {
            if (document.visibilityState === "visible") void updatePresence();
          };
          document.addEventListener("visibilitychange", presenceVisHandler);
          if (typeof window !== "undefined") {
            window.addEventListener("focus", presenceVisHandler);
          }
        }
      } else {
        beklemeyiDurdur();
        set({
          user: null,
          currentUserProfile: null,
          loading: false,
          profileResolved: false,
          profileMissing: false,
          profileUnreachable: false,
          profileRetrying: false,
        });
      }
    });

    // Cleanup fonksiyonu
    return () => {
      unsubAuth();
      beklemeyiDurdur();
      if (profileUnsub) profileUnsub();
      if (presenceVisHandler) {
        if (typeof document !== "undefined") document.removeEventListener("visibilitychange", presenceVisHandler);
        if (typeof window !== "undefined") window.removeEventListener("focus", presenceVisHandler);
        presenceVisHandler = null;
      }
    };
  },
}));

// Selector'lar — gereksiz re-render'ı önler
export const selectUser = (s: KimlikDurumu) => s.user;
export const selectProfile = (s: KimlikDurumu) => s.currentUserProfile;
export const selectLoading = (s: KimlikDurumu) => s.loading;
export const selectInitAuth = (s: KimlikDurumu) => s.initAuth;
